import PropTypes from "prop-types";
import React from "react";
import { Badge, Button, Card, Col, Container, Row } from "react-bootstrap";

import { TestimonialSection } from "./TestimonialCard";

export const InvestmentBreakdown = ({ puppy }) => {
  // Parse the price to extract numeric value
  const parsePrice = (priceString) => {
    if (!priceString) return 0;
    const numericValue = priceString.replace(/[^0-9]/g, "");
    return parseInt(numericValue, 10) || 0;
  };

  const totalPrice = parsePrice(puppy.price);

  // Professional investment breakdown
  const getInvestmentBreakdown = () => {
    const puppyCost = Math.round(totalPrice * 0.35); // 35% puppy cost
    const trainingProgram = Math.round(totalPrice * 0.45); // 45% comprehensive training
    const healthAndCertification = Math.round(totalPrice * 0.12); // 12% health/certification
    const supportAndGuarantees = Math.round(totalPrice * 0.08); // 8% lifetime support

    return {
      puppyCost,
      trainingProgram,
      healthAndCertification,
      supportAndGuarantees,
      totalInvestment: totalPrice,
      comparativeValue: Math.round(totalPrice * 1.8), // Show market comparison
      includes: [
        "Premium breeding selection and genetic health screening",
        "Comprehensive 8-week professional training program",
        "Advanced temperament assessment by certified behaviorists",
        "Complete house training, crate training, and basic obedience",
        "Extensive socialization program with people, animals, and environments",
        "Professional in-home delivery and integration consultation",
        "Premium training equipment and supplies package",
        "Complete health certification and current vaccination protocol",
        "Lifetime professional support via phone, email, and video calls",
        "30-day satisfaction guarantee with training adjustment options",
        "Access to exclusive alumni training resources and community",
        "Annual wellness check-in and behavioral consultation",
      ],
    };
  };

  const getProfessionalGuarantees = () => {
    return [
      {
        title: "Professional Training Assurance",
        description:
          "If your puppy doesn't demonstrate the trained behaviors within 30 days, we provide additional professional training at no cost.",
        coverage: "Comprehensive behavioral training guarantee",
        duration: "30 days",
      },
      {
        title: "Health & Genetic Warranty",
        description:
          "Comprehensive health guarantee covering genetic conditions with full veterinary support for the first two years.",
        coverage: "Genetic health conditions and congenital issues",
        duration: "2 years",
      },
      {
        title: "Satisfaction Commitment",
        description:
          "If you're not completely satisfied with your puppy's training and temperament, we'll work with you until you are.",
        coverage: "Complete satisfaction with training outcomes",
        duration: "Lifetime",
      },
      {
        title: "Professional Support Network",
        description:
          "Unlimited access to our team of certified trainers and behaviorists for guidance throughout your dog's life.",
        coverage: "Professional behavioral consultation and support",
        duration: "Lifetime",
      },
    ];
  };

  const getValueComparison = () => {
    const marketComparison = {
      puppyOnly: Math.round(totalPrice * 0.6), // Traditional puppy cost
      separateTraining: Math.round(totalPrice * 0.8), // Professional training separately
      totalSeparate: Math.round(totalPrice * 1.4), // Combined separate costs
      ourProgram: totalPrice,
      savings: Math.round(totalPrice * 0.4), // Savings with our program
    };
    return marketComparison;
  };

  const breakdown = getInvestmentBreakdown();
  const guarantees = getProfessionalGuarantees();
  const valueComparison = getValueComparison();

  return (
    <div className="investment-breakdown">
      <Container>
        {/* Professional Header */}
        <Row className="mb-5">
          <Col lg={8} className="mx-auto text-center">
            <h2 className="section-title mb-3">Investment & Value Breakdown</h2>
            <p className="section-subtitle text-muted">
              Transparent pricing for {puppy.cardHeaderTitle} with comprehensive
              breakdown of services, guarantees, and exceptional value
              proposition.
            </p>
          </Col>
        </Row>

        {/* Main Investment Display */}
        <Row className="mb-5">
          <Col lg={10} className="mx-auto">
            <Card className="investment-main-card">
              <Card.Body className="p-5">
                <div className="investment-header text-center mb-4">
                  <div className="total-investment">
                    <h3 className="investment-amount">
                      ${totalPrice.toLocaleString()}
                    </h3>
                    <p className="investment-subtitle">
                      Complete Training Program Investment
                    </p>
                  </div>
                  <div className="value-comparison mt-3">
                    <span className="market-value">
                      Market Value: $
                      {valueComparison.totalSeparate.toLocaleString()}
                    </span>
                    <Badge bg="success" className="savings-badge">
                      Save ${valueComparison.savings.toLocaleString()}
                    </Badge>
                  </div>
                </div>

                <Row className="investment-breakdown-details">
                  <Col md={6} lg={3} className="mb-3">
                    <div className="breakdown-item">
                      <div className="breakdown-amount">
                        ${breakdown.puppyCost.toLocaleString()}
                      </div>
                      <div className="breakdown-label">Premium Puppy</div>
                      <div className="breakdown-description">
                        Genetically screened, health certified
                      </div>
                    </div>
                  </Col>
                  <Col md={6} lg={3} className="mb-3">
                    <div className="breakdown-item">
                      <div className="breakdown-amount">
                        ${breakdown.trainingProgram.toLocaleString()}
                      </div>
                      <div className="breakdown-label">Training Program</div>
                      <div className="breakdown-description">
                        8-week comprehensive professional training
                      </div>
                    </div>
                  </Col>
                  <Col md={6} lg={3} className="mb-3">
                    <div className="breakdown-item">
                      <div className="breakdown-amount">
                        ${breakdown.healthAndCertification.toLocaleString()}
                      </div>
                      <div className="breakdown-label">
                        Health & Certification
                      </div>
                      <div className="breakdown-description">
                        Complete health screening and documentation
                      </div>
                    </div>
                  </Col>
                  <Col md={6} lg={3} className="mb-3">
                    <div className="breakdown-item">
                      <div className="breakdown-amount">
                        ${breakdown.supportAndGuarantees.toLocaleString()}
                      </div>
                      <div className="breakdown-label">Lifetime Support</div>
                      <div className="breakdown-description">
                        Professional guidance and guarantees
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* What's Included Section */}
        <Row className="mb-5">
          <Col lg={10} className="mx-auto">
            <Card className="includes-card">
              <Card.Body className="p-4">
                <h4 className="text-center mb-4">Complete Program Includes</h4>
                <Row>
                  {breakdown.includes.map((item, index) => (
                    <Col md={6} key={index} className="mb-2">
                      <div className="include-item">
                        <span className="include-check">✓</span>
                        <span className="include-text">{item}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Professional Guarantees */}
        <Row className="mb-5">
          <Col lg={10} className="mx-auto">
            <h4 className="text-center mb-4">
              Professional Guarantees & Warranties
            </h4>
            <Row>
              {guarantees.map((guarantee, index) => (
                <Col md={6} key={index} className="mb-4">
                  <Card className="guarantee-card">
                    <Card.Body className="p-4">
                      <h5 className="guarantee-title">{guarantee.title}</h5>
                      <p className="guarantee-description">
                        {guarantee.description}
                      </p>
                      <div className="guarantee-details">
                        <div className="guarantee-coverage">
                          <strong>Coverage:</strong> {guarantee.coverage}
                        </div>
                        <div className="guarantee-duration">
                          <strong>Duration:</strong> {guarantee.duration}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        {/* Value Comparison Table */}
        <Row className="mb-5">
          <Col lg={8} className="mx-auto">
            <Card className="comparison-card">
              <Card.Body className="p-4">
                <h4 className="text-center mb-4">Value Comparison Analysis</h4>
                <div className="comparison-table">
                  <div className="comparison-row header-row">
                    <div className="comparison-label">Service</div>
                    <div className="comparison-separate">
                      Purchased Separately
                    </div>
                    <div className="comparison-program">Our Program</div>
                  </div>
                  <div className="comparison-row">
                    <div className="comparison-label">Premium Puppy</div>
                    <div className="comparison-separate">
                      ${valueComparison.puppyOnly.toLocaleString()}
                    </div>
                    <div className="comparison-program">Included</div>
                  </div>
                  <div className="comparison-row">
                    <div className="comparison-label">
                      Professional Training (8 weeks)
                    </div>
                    <div className="comparison-separate">
                      ${valueComparison.separateTraining.toLocaleString()}
                    </div>
                    <div className="comparison-program">Included</div>
                  </div>
                  <div className="comparison-row">
                    <div className="comparison-label">
                      Health Certification & Support
                    </div>
                    <div className="comparison-separate">$800</div>
                    <div className="comparison-program">Included</div>
                  </div>
                  <div className="comparison-row">
                    <div className="comparison-label">
                      Lifetime Professional Support
                    </div>
                    <div className="comparison-separate">$1,200+</div>
                    <div className="comparison-program">Included</div>
                  </div>
                  <div className="comparison-row total-row">
                    <div className="comparison-label">
                      <strong>Total Investment</strong>
                    </div>
                    <div className="comparison-separate">
                      <strong>
                        ${valueComparison.totalSeparate.toLocaleString()}
                      </strong>
                    </div>
                    <div className="comparison-program program-total">
                      <strong>${totalPrice.toLocaleString()}</strong>
                    </div>
                  </div>
                  <div className="savings-row">
                    <div className="savings-highlight">
                      <strong>
                        Your Savings: $
                        {valueComparison.savings.toLocaleString()}
                      </strong>
                      <span className="savings-percentage">
                        (
                        {Math.round(
                          (valueComparison.savings /
                            valueComparison.totalSeparate) *
                            100,
                        )}
                        % savings)
                      </span>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Call-to-Action Section */}
        <Row className="mb-5">
          <Col lg={6} className="mx-auto text-center">
            <Card className="cta-card">
              <Card.Body className="p-4">
                <h4 className="mb-3">
                  Ready to Welcome {puppy.cardHeaderTitle}?
                </h4>
                <p className="text-muted mb-4">
                  Secure your investment in a professionally trained, certified
                  companion. Limited availability with our comprehensive
                  training program.
                </p>
                <div className="cta-buttons">
                  <Button
                    variant="primary"
                    size="lg"
                    className="cta-primary me-3"
                    href="sms:+13179546952?body=Hi Justine! I'd like to schedule a consultation about your training program."
                  >
                    Schedule Consultation
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="lg"
                    className="cta-secondary"
                    href="/contact-us"
                  >
                    Request Information
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <TestimonialSection />
      </Container>
    </div>
  );
};

InvestmentBreakdown.propTypes = {
  puppy: PropTypes.object.isRequired,
};
